import React, { useRef, useEffect } from 'react';

export interface ScrollEndInfo {
  scrollLeft: number;
  scrollTop: number;
}

interface ScrollViewProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onScrollEnd'> {
  /**
   * 滚动结束回调（防抖触发）
   */
  onScrollEnd?: (info: ScrollEndInfo) => void;
  /**
   * 滚动结束判定延迟（毫秒），默认 200ms
   */
  scrollEndDelay?: number;
}

/**
 * 通用滚动容器，支持滚动结束事件
 */
const ScrollView: React.FC<ScrollViewProps> = ({
  onScrollEnd,
  scrollEndDelay = 200,
  onScroll,
  className,
  style,
  children,
  ...restProps
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    // 先透传原始 onScroll
    if (onScroll) {
      onScroll(event);
    }

    if (!onScrollEnd) return;

    const target = event.currentTarget;

    // 重置之前的计时器
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      onScrollEnd({
        scrollLeft: target.scrollLeft,
        scrollTop: target.scrollTop,
      });
    }, scrollEndDelay);
  };

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      onScroll={handleScroll}
      {...restProps}
    >
      {children}
    </div>
  );
};

export default ScrollView;
